import { useCallback, useEffect, useState } from 'react'
import GeoJSON from 'geojson'
import { Point } from 'mapbox-gl'

import { InjectionTokens } from '@/controller/tokens'

import { MapboxCoordinates } from '@/domain/protocols/Coordinates'

import { IMeteorologyRepository, MeteorologyManifest } from '@/data/MeteorologyRepository/IMeteorologyRepository'

import { MapMeteorologyGeoJsonLayer } from '../components/MapMeteorologyLayer/MapMeteorologyGeoJsonLayer'
import { MapMeteorologyRasterLayer } from '../components/MapMeteorologyLayer/MapMeteorologyRasterLayer'
import { ignoreMapClickLayers, isAirspaceInteractiveLayer } from '../components/MapView/constants'
import { useMapContext } from '../components/MapView/MapContext'
import { FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { MeteorologyState, MeteorologyStateProps } from '../states/MeteorologyState'

import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { createCircleFeature } from '@/presentation/modules/MapScreen/utils/meteorology'
import { useMeteorologyManifestQuery } from '@/presentation/queries/useMeteorologyManifestQuery'

function MapMeteorologyPresenter() {
  const meteorologyRepository = useInjection<IMeteorologyRepository>(InjectionTokens.MeteorologyRepository)

  const meteorologyState = useBehaviorSubject<MeteorologyStateProps>(MeteorologyState)
  // O manifest era buscado a cada frame da animacao (aqui e de novo dentro do getGeoJson). Agora vem
  // da query compartilhada, com staleTime.
  const manifestQuery = useMeteorologyManifestQuery(meteorologyState.layer)
  const manifest = manifestQuery.data

  const [featureType, setFeatureType] = useState<MeteorologyManifest['type']>(null)
  const [featureStyle, setFeatureStyle] = useState<MeteorologyManifest['featureStyle']>(null)
  const [geoJson, setGeoJson] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection)
  const [rasterFile, setRasterFile] = useState<string>(null)
  const [rasterCoordinates, setRasterCoordinates] = useState<MapboxCoordinates>(null)

  const mapContext = useMapContext()

  useEffect(() => {
    const resetState = () => {
      setFeatureType(null)
      setFeatureStyle(null)
      setGeoJson({} as GeoJSON.FeatureCollection)
      setRasterFile(null)
      setRasterCoordinates(null)
    }

    const setNothingToShow = () => {
      resetState()
    }

    const setShowError = () => {
      resetState()
    }

    const setShowGeoJSONSuccess = (
      collection: GeoJSON.FeatureCollection,
      featureStyle: MeteorologyManifest['featureStyle']
    ) => {
      setFeatureType('geojson')
      setFeatureStyle(featureStyle)
      setGeoJson(collection)
      setRasterFile(null)
      setRasterCoordinates(null)
    }

    const setShowRasterSuccess = (rasterFile: string, rasterCoordinates: MapboxCoordinates) => {
      setFeatureType('raster')
      setFeatureStyle(null)
      setGeoJson({} as GeoJSON.FeatureCollection)
      setRasterFile(rasterFile)
      setRasterCoordinates(rasterCoordinates)
    }

    const run = async () => {
      if (!meteorologyState.layer || !meteorologyState.time) {
        setNothingToShow()
        return
      }

      // Aguarda o manifest sem tocar no estado: mexer aqui apagaria a camada visivel a cada troca de
      // frame enquanto a query nao resolvesse.
      if (manifestQuery.isPending) {
        return
      }

      if (!manifest) {
        setShowError()
        return
      }

      try {
        if (manifest.type === 'geojson') {
          const geojsonData = await meteorologyRepository.getGeoJson(
            meteorologyState.layer,
            meteorologyState.time,
            manifest.frameTimestamps
          )

          if (geojsonData.isFailure) {
            setShowError()
            return
          }

          if (manifest.featureStyle === 'point') {
            const multiPolygonFeatures = geojsonData.getValue().features.map((feature) => {
              return createCircleFeature(feature as GeoJSON.Feature<GeoJSON.MultiPoint>)
            })

            setShowGeoJSONSuccess(
              {
                ...geojsonData.getValue(),
                features: multiPolygonFeatures
              },
              manifest.featureStyle
            )

            return
          }

          setShowGeoJSONSuccess(geojsonData.getValue(), manifest.featureStyle)
          return
        } else if (manifest.type === 'raster') {
          const RasterData = `${import.meta.env.VITE_METEOROLOGY_PACKAGES_API_BASEURL}/${meteorologyState.layer}/${meteorologyState.time}.png`

          const bounds = manifest.boundaries
          const rasterCoordinates: MapboxCoordinates = [
            [bounds[0], bounds[3]],
            [bounds[2], bounds[3]],
            [bounds[2], bounds[1]],
            [bounds[0], bounds[1]]
          ]

          setShowRasterSuccess(RasterData, rasterCoordinates)
          return
        }

        setNothingToShow()
      } catch (error) {
        setShowError()
      }
    }

    run()
  }, [meteorologyState.layer, meteorologyState.time, manifest, manifestQuery.isPending, meteorologyRepository])

  const handleFeaturePress = useCallback(async (features: GeoJSON.Feature[], point: Point) => {
    const ignoredLayers: string[] = []
    mapContext.map.getStyle().layers.forEach((layer) => {
      if (
        (ignoreMapClickLayers.includes(layer.id) || isAirspaceInteractiveLayer(layer.id)) &&
        !layer.id.includes('sigmet')
      ) {
        ignoredLayers.push(layer.id)
      }
    })
    const ignoredFeatures = mapContext.map.queryRenderedFeatures(point, {
      layers: ignoredLayers
    })
    if (ignoredFeatures.length > 0) return
    const titles: string[] = []
    const contents: string[] = []

    for (const feature of features) {
      if (feature.properties?.tooltipTitle && !titles.includes(feature.properties.tooltipTitle)) {
        titles.push(feature.properties.tooltipTitle)
      }

      if (feature.properties?.tooltipContent && !contents.includes(feature.properties.tooltipContent)) {
        contents.push(feature.properties.tooltipContent)
      }
    }

    if (titles.length === 0 || contents.length === 0) {
      FeatureInfoStateMutator.hide()
      return
    }
    FeatureInfoStateMutator.setInfo(titles.join(' / '), contents.join('\n\n'))
  }, [])

  if (!meteorologyState.layer) {
    return null
  }

  if (featureType === 'geojson' && featureStyle && featureStyle !== 'raster' && geoJson && geoJson.features) {
    return (
      <>
        {meteorologyState.layer === 'sigmet' && (
          <MapMeteorologyGeoJsonLayer
            id={'sigmet'}
            featureStyle={featureStyle}
            geoJson={geoJson}
            onClick={handleFeaturePress}
          />
        )}
        {meteorologyState.layer === 'stsc' && (
          <MapMeteorologyGeoJsonLayer
            id={'stsc'}
            featureStyle={featureStyle}
            geoJson={geoJson}
            onClick={handleFeaturePress}
          />
        )}
      </>
    )
  }

  if (featureType === 'raster' && rasterFile && rasterCoordinates) {
    return (
      <>
        {meteorologyState.layer === 'satellite-visible' && (
          <MapMeteorologyRasterLayer id={'satellite-visible'} url={rasterFile} coordinates={rasterCoordinates} />
        )}
        {meteorologyState.layer === 'satellite-infrared' && (
          <MapMeteorologyRasterLayer id={'satellite-infrared'} url={rasterFile} coordinates={rasterCoordinates} />
        )}
        {meteorologyState.layer === 'satellite-highlight' && (
          <MapMeteorologyRasterLayer id={'satellite-highlight'} url={rasterFile} coordinates={rasterCoordinates} />
        )}
        {meteorologyState.layer === 'radar-maxcappi' && (
          <MapMeteorologyRasterLayer id={'radar-maxcappi'} url={rasterFile} coordinates={rasterCoordinates} />
        )}
        {meteorologyState.layer === 'sigwx' && (
          <MapMeteorologyRasterLayer id={'sigwx'} url={rasterFile} coordinates={rasterCoordinates} />
        )}
      </>
    )
  }
}

export { MapMeteorologyPresenter }
